// ====================================================================
// ziven-core forum PhoneLogin - v0.5.0 (Trello 卡 6a7e65df8eb1684c9d8b78f8)
// ====================================================================
//
// 覆盖 vendor LogInModal 加手机号登录 tab 切换 (辉哥 08:30 拍板 1A 验证码模式 MVP).
// 实现方式: extend vendor LogInModal.prototype (辉哥 08:47 拍板 2A 小改动模式),
// 不是 override. 维护成本低, vendor 升级兼容好.
//
// v0.5.0 关键 bug fix (Coder 排查沉淀):
//   Flarum 2.0 vendor LogInModal 走 lazy chunk 加载, `flarum.reg.get('core', ...)`
//   在 chunk 未 load 时返回 undefined. 直接 `extend(LogInModal.prototype, ...)` 会
//   crash 'Cannot read properties of undefined (reading prototype)'.
//
//   修法: 改用 `flarum.reg.onLoad('core', 'forum/components/LogInModal', callback)`,
//   callback 拿到 module 后再 extend. 模块同步注册时 callback 立即调用, 异步注册
//   时等 chunk load 完再调用 (vendor 走 flarum.reg.loadChunk 拉 JS 文件).
//
// 设计:
//   - 加 this.phoneMode Stream (false = 邮箱模式, true = 手机号模式)
//   - 加 this.phoneNumber / this.phoneCode Stream (跟 vendor identification/password 一致)
//   - extend 'fields' 加 'phone-tab' (顶部 tab 切换 UI) + 'phone-input' (手机号 input)
//     + 'phone-code' (验证码 input + 发送按钮) + 'phone-submit' (登录按钮)
//   - 加 sendSmsCode() (POST /api/ziven/core/phone/send-code) + phoneLogin()
//     (POST /api/ziven/core/phone/login) 方法
//   - 加 60s 倒计时 (防止用户连点发短信按钮)
//
// 跟 kater-community-signup 同款 pattern (extend fields + 加新 item), 但:
//   - 不隐藏原 email mode 字段, 用 weight 排序 + 条件渲染 (visibility)
//   - 加 tab 切换 UI (跟 v0.4.9 admin ZivenCoreSettingsPage 同款 UX)
//   - 走 ziven-core 自己的 API (不走 vendor session.login)
//
// 调用方: js/src/forum/index.js
// ====================================================================
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Stream from 'flarum/common/utils/Stream';
import Button from 'flarum/common/components/Button';
// v0.5.2 (Trello 卡 6a7fc343f990e73904af2c75): 抽公共 helpers 给 SignUp.js 复用
// (sendSmsCode / startCountdown / startPasswordLockCountdown / clearTimers)
import { sendSmsCode, startCountdown, startPasswordLockCountdown, clearTimers } from '../utils/phoneAuthHelpers';
export default function phoneLoginExtension() {
  // LogInModal 在 vendor 是 lazy chunk 加载, 用 onLoad 等 module 加载完再 extend
  // flarum.reg 是 global, app 是 import 的 (不挂 flarum.reg 引用)
  const flarumReg = typeof window !== 'undefined' && window.flarum && window.flarum.reg || typeof flarum !== 'undefined' && flarum.reg || null;
  if (!flarumReg) {
    console.warn('[ziven-core] phoneLoginExtension: flarum.reg not available, skipped');
    return;
  }

  // 尝试立即拿 (可能已经同步注册)
  let LogInModal = flarumReg.get('core', 'forum/components/LogInModal');
  if (LogInModal && LogInModal.default) {
    LogInModal = LogInModal.default;
  }
  if (LogInModal) {
    applyExtensions(LogInModal);
  } else {
    // 模块未加载, 用 onLoad 等待
    flarumReg.onLoad('core', 'forum/components/LogInModal', mod => {
      const cls = mod && mod.default ? mod.default : mod;
      if (cls) {
        applyExtensions(cls);
      } else {
        console.warn('[ziven-core] phoneLoginExtension: LogInModal module loaded but empty');
      }
    });
  }
}
function applyExtensions(LogInModal) {
  if (!LogInModal || !LogInModal.prototype) {
    console.warn('[ziven-core] phoneLoginExtension: LogInModal has no prototype, skipped');
    return;
  }

  // ─────────────────────────────────────────────────────────────
  // oninit: 加 phone 模式状态 + phone 字段 Stream
  // ── v0.5.1 (Trello 卡 6a7f157dd1aaef7abfcc0cab, 辉哥 21:17 拍板) ──────────
  //   加 phone 密码模式 Stream (二次 extend, 跟 v0.5.0 验证码模式共存):
  //   - phoneSubMode: false = 验证码模式 (v0.5.0), true = 密码模式 (v0.5.1)
  //   - phonePassword: 密码值 (跟 vendor password Stream 同款)
  //   - phonePasswordSending: 登录请求中 (loading state)
  //   - phonePasswordLockCountdown: 锁定倒计时秒数 (0 = 未锁, 5连错锁 10min)
  // ─────────────────────────────────────────────────────────────
  extend(LogInModal.prototype, 'oninit', function () {
    this.phoneMode = Stream(false);
    this.phoneNumber = Stream('');
    this.phoneCode = Stream('');
    this.phoneSending = Stream(false);
    this.phoneCountdown = Stream(0); // 倒计时秒数, 0 = 可发
    // v0.5.1 密码模式 (4 Stream, 1A 拍板二次 extend)
    this.phoneSubMode = Stream(false); // false=验证码, true=密码
    this.phonePassword = Stream('');
    this.phonePasswordSending = Stream(false);
    this.phonePasswordLockCountdown = Stream(0); // 锁定倒计时
  });

  // ─────────────────────────────────────────────────────────────
  // fields: 加 phone tab 切换 + phone mode 字段
  // ─────────────────────────────────────────────────────────────
  extend(LogInModal.prototype, 'fields', function (items) {
    const phoneLoginEnabled = app.forum.attribute('zivenCorePhoneLoginEnabled');

    // 1. 顶部 tab 切换 UI (weight 100, 永远显示 — 如果 phone_login_enabled 关, 整体不显示)
    if (phoneLoginEnabled) {
      items.add('phone-tab', m("div", {
        className: "Form-group ziven-core-login-tabs"
      }, m("button", {
        type: "button",
        className: 'ziven-core-login-tab ' + (!this.phoneMode() ? 'active' : ''),
        onclick: () => this.phoneMode(false)
      }, app.translator.trans('ziven-community-core.forum.login_tab_email')), m("button", {
        type: "button",
        className: 'ziven-core-login-tab ' + (this.phoneMode() ? 'active' : ''),
        onclick: () => this.phoneMode(true)
      }, app.translator.trans('ziven-community-core.forum.login_tab_phone'))), 100);
    }

    // 2. phone mode 时, 隐藏原 email/password/remember/submit
    //    ── v0.5.1.a 修法 (Mavis 终审反馈 v0.5.0 wrap div over-engineering, Flarum
    //    ItemList + mithril 0.2.x 重渲时缓存原始 content 引用, wrap div 实际从未
    //    渲染, vendor input/remember/submit 一直显示). 改用 items.remove API
    //    (Flarum 稳定 API, 不依赖 mithril vnode 引用), 简单稳.
    if (phoneLoginEnabled && this.phoneMode()) {
      const hideKeys = ['identification', 'password', 'remember', 'submit'];
      hideKeys.forEach(k => {
        if (items.has(k)) {
          items.remove(k);
        }
      });
    }

    // 3. phone mode: 子 tab (验证码 / 密码) + phone input + (code 或 password) + 提交
    //    ── v0.5.1 (Trello 卡 6a7f157dd1aaef7abfcc0cab, 辉哥 21:17 拍板) ──────
    //    1A 拍板: 二次 extend wrap vnode, 在 phoneMode 内部加 phoneSubMode 子 tab
    //    验证码模式 (subMode=false) 走 v0.5.0 sendSmsCode + phoneLogin
    //    密码模式 (subMode=true) 走 v0.5.1 phonePasswordLogin
    //    ─────────────────────────────────────────────────────────────────────
    if (phoneLoginEnabled && this.phoneMode()) {
      const phoneLabel = app.translator.trans('ziven-community-core.forum.login_phone_placeholder');
      const codeLabel = app.translator.trans('ziven-community-core.forum.login_code_placeholder');
      const passwordLabel = app.translator.trans('ziven-community-core.forum.login_password_placeholder');

      // 3.1 子 tab 切换 (验证码 / 密码) — weight 95, 在 phone input 上方
      items.add('phone-sub-tab', m("div", {
        className: "Form-group ziven-core-sub-tabs"
      }, m("button", {
        type: "button",
        className: 'ziven-core-sub-tab ' + (!this.phoneSubMode() ? 'active' : ''),
        onclick: () => this.phoneSubMode(false)
      }, app.translator.trans('ziven-community-core.forum.login_sub_tab_code')), m("button", {
        type: "button",
        className: 'ziven-core-sub-tab ' + (this.phoneSubMode() ? 'active' : ''),
        onclick: () => this.phoneSubMode(true)
      }, app.translator.trans('ziven-community-core.forum.login_sub_tab_password'))), 95);

      // 3.2 phone input (公共, 验证码密码模式都用)
      items.add('phone-input', m("div", {
        className: "Form-group"
      }, m("input", {
        className: "FormControl",
        name: "phone",
        type: "tel",
        inputMode: "numeric",
        maxlength: "11",
        autocomplete: "tel",
        placeholder: phoneLabel,
        "aria-label": phoneLabel,
        bidi: this.phoneNumber,
        disabled: this.loading || this.phonePasswordLockCountdown() > 0
      })), 30);

      // 3.3 验证码模式: code input + 发送按钮 (v0.5.0 现状)
      if (!this.phoneSubMode()) {
        items.add('phone-code', m("div", {
          className: "Form-group ziven-core-phone-code-row"
        }, m("input", {
          className: "FormControl",
          name: "code",
          type: "text",
          inputMode: "numeric",
          maxlength: "6",
          autocomplete: "one-time-code",
          placeholder: codeLabel,
          "aria-label": codeLabel,
          bidi: this.phoneCode,
          disabled: this.loading
        }), m("button", {
          type: "button",
          className: "Button ziven-core-send-code-btn",
          disabled: this.loading || this.phoneSending() || this.phoneCountdown() > 0,
          onclick: this.sendSmsCode.bind(this)
        }, this.phoneCountdown() > 0 ? app.translator.trans('ziven-community-core.forum.login_resend_countdown', {
          seconds: this.phoneCountdown()
        }) : this.phoneSending() ? app.translator.trans('ziven-community-core.forum.login_sending') : app.translator.trans('ziven-community-core.forum.login_send_code'))), 15);
      }

      // 3.4 密码模式: password input (v0.5.1 新增)
      if (this.phoneSubMode()) {
        const lockSeconds = this.phonePasswordLockCountdown();
        items.add('phone-password', m("div", {
          className: "Form-group"
        }, m("input", {
          className: "FormControl ziven-core-password-input",
          name: "phone-password",
          type: "password",
          autocomplete: "current-password",
          maxlength: "32",
          placeholder: lockSeconds > 0 ? app.translator.trans('ziven-community-core.forum.login_password_locked', {
            minutes: Math.ceil(lockSeconds / 60)
          }) : passwordLabel,
          "aria-label": passwordLabel,
          bidi: this.phonePassword,
          disabled: this.loading || lockSeconds > 0
        })), 15);
      }
      items.add('phone-submit', m("div", {
        className: "Form-group"
      }, m(Button, {
        className: "Button Button--primary Button--block",
        type: "submit",
        loading: this.loading || this.phonePasswordSending(),
        disabled: this.phoneSubMode() && this.phonePasswordLockCountdown() > 0,
        onclick: this.phoneSubMode() ? this.phonePasswordLogin.bind(this) : this.phoneLogin.bind(this)
      }, this.phoneSubMode() ? app.translator.trans('ziven-community-core.forum.login_with_phone_password') : app.translator.trans('ziven-community-core.forum.login_with_phone'))), -10);

      // 记住我 (phone mode 走 remember)
      items.add('phone-remember', m("div", {
        className: "Form-group"
      }, m("div", null, m("label", {
        className: "checkbox"
      }, m("input", {
        type: "checkbox",
        bidi: this.remember,
        disabled: this.loading
      }), app.translator.trans('core.forum.log_in.remember_me_label')))), 10);
    }
  });

  // ─────────────────────────────────────────────────────────────
  // onsubmit: phone mode 拦截, 走 phoneLogin / phonePasswordLogin (v0.5.1 区分)
  // ── v0.5.0 修法: 用 override 而非 extend (v0.5.0 SOP bug, 实际 e 是 undefined)
  //   原因: extend 的 callback signature 是 (originalReturnValue, ...args),
  //   原写法 `function (e)` 把 e 当成 original return value 错位. override 正确签名
  //   是 (original, ...args), 我们 function (original, e) 才正确接 e.
  // ── v0.5.1 (Trello 卡 6a7f157dd1aaef7abfcc0cab, 辉哥 21:17 拍板) ──────────
  //   phoneSubMode=false → 验证码模式 → phoneLogin
  //   phoneSubMode=true  → 密码模式   → phonePasswordLogin
  // ─────────────────────────────────────────────────────────────
  override(LogInModal.prototype, 'onsubmit', function (original, e) {
    if (this.phoneMode && this.phoneMode()) {
      if (e && e.preventDefault) e.preventDefault();
      if (this.phoneSubMode && this.phoneSubMode()) {
        this.phonePasswordLogin();
      } else {
        this.phoneLogin();
      }
      return;
    }
    return original(e);
  });

  // ─────────────────────────────────────────────────────────────
  // 发送验证码 — POST /api/ziven/core/phone/send-code
  // ── v0.5.2: 抽公共 helper (js/src/forum/utils/phoneAuthHelpers.js)
  //   行为跟 v0.5.0/v0.5.1 100% 一致, 走 helper 复用
  // ─────────────────────────────────────────────────────────────
  LogInModal.prototype.sendSmsCode = function () {
    sendSmsCode(this);
  };

  // ─────────────────────────────────────────────────────────────
  // 倒计时 (60s 防止连点) — v0.5.2 抽公共 helper
  // ─────────────────────────────────────────────────────────────
  LogInModal.prototype.startCountdown = function () {
    startCountdown(this);
  };

  // ─────────────────────────────────────────────────────────────
  // 手机号 + 验证码登录 — POST /api/ziven/core/phone/login
  // ─────────────────────────────────────────────────────────────
  LogInModal.prototype.phoneLogin = function () {
    const phone = (this.phoneNumber() || '').trim();
    const code = (this.phoneCode() || '').trim();
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('ziven-community-core.forum.login_error_invalid_phone'));
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('ziven-community-core.forum.login_error_invalid_code'));
      return;
    }
    this.loading = true;
    m.redraw();
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/ziven/core/phone/login',
      body: {
        phone,
        code,
        remember: this.remember()
      },
      errorHandler: err => {
        this.loading = false;
        m.redraw();
        const code = err && err.status;
        if (code === 401) {
          app.alerts.show({
            type: 'error'
          }, app.translator.trans('ziven-community-core.forum.login_error_code_invalid'));
        } else if (code === 404) {
          app.alerts.show({
            type: 'error'
          }, app.translator.trans('ziven-community-core.forum.login_error_user_not_found'));
        } else {
          const msg = err && err.alert && err.alert.content || app.translator.trans('ziven-community-core.forum.login_error_login_failed');
          app.alerts.show({
            type: 'error'
          }, msg);
        }
      }
    }).then(response => {
      if (response && response.success && response.data) {
        // 服务端 session 已落, reload 走 Flarum session
        window.location.reload();
      }
    }).catch(() => {
      this.loading = false;
      m.redraw();
    });
  };

  // ─────────────────────────────────────────────────────────────
  // onremove: 清掉倒计时 timer 避免内存泄漏
  // ── v0.5.1 (Trello 卡 6a7f157dd1aaef7abfcc0cab, 辉哥 21:17 拍板) ──────────
  //   +1 个 lock countdown timer (5连错锁 10min 倒计时)
  // ── v0.5.2 (Trello 卡 6a7fc343f990e73904af2c75): 抽 clearTimers helper 复用
  // ─────────────────────────────────────────────────────────────
  extend(LogInModal.prototype, 'onremove', function () {
    clearTimers(this);
  });

  // ─────────────────────────────────────────────────────────────
  // v0.5.1 (Trello 卡 6a7f157dd1aaef7abfcc0cab, 辉哥 21:17 拍板) — 手机号+密码登录
  // ─────────────────────────────────────────────────────────────
  // POST /api/ziven/core/phone/login-password
  // 限流 1min/3次 + 1day/30次 + 5连错锁 10min (跟 v0.5.0 验证码模式解耦)
  // ─────────────────────────────────────────────────────────────
  LogInModal.prototype.phonePasswordLogin = function () {
    const phone = (this.phoneNumber() || '').trim();
    const password = this.phonePassword() || '';

    // 前端基本校验
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('ziven-community-core.forum.login_error_invalid_phone'));
      return;
    }
    if (password.length < 6 || password.length > 32) {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('ziven-community-core.forum.login_error_invalid_password'));
      return;
    }

    // 锁定检查 (前端防御性, 真锁定靠服务端)
    if (this.phonePasswordLockCountdown() > 0) {
      const minutes = Math.ceil(this.phonePasswordLockCountdown() / 60);
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('ziven-community-core.forum.login_error_password_locked', {
        minutes
      }));
      return;
    }
    this.phonePasswordSending(true);
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/ziven/core/phone/login-password',
      body: {
        phone,
        password,
        remember: this.remember()
      },
      errorHandler: err => {
        this.phonePasswordSending(false);
        m.redraw();
        const code = err && err.status;
        if (code === 401) {
          app.alerts.show({
            type: 'error'
          }, app.translator.trans('ziven-community-core.forum.login_error_password_wrong'));
        } else if (code === 423) {
          // 锁定 — 启动前端倒计时
          const lockRemaining = err && err.alert && err.alert.content && err.alert.content.lockRemaining || 600;
          this.startPasswordLockCountdown(lockRemaining);
          const minutes = Math.ceil(lockRemaining / 60);
          app.alerts.show({
            type: 'error'
          }, app.translator.trans('ziven-community-core.forum.login_error_password_locked', {
            minutes
          }));
        } else if (code === 429) {
          app.alerts.show({
            type: 'error'
          }, app.translator.trans('ziven-community-core.forum.login_error_rate_limited'));
        } else if (code === 403) {
          app.alerts.show({
            type: 'error'
          }, app.translator.trans('ziven-community-core.forum.login_error_disabled'));
        } else {
          const msg = err && err.alert && err.alert.content || app.translator.trans('ziven-community-core.forum.login_error_login_failed');
          app.alerts.show({
            type: 'error'
          }, msg);
        }
      }
    }).then(response => {
      this.phonePasswordSending(false);
      if (response && response.success && response.data) {
        // 服务端 session 已落, reload 走 Flarum session
        window.location.reload();
      }
    }).catch(() => {
      this.phonePasswordSending(false);
      m.redraw();
    });
  };

  // ─────────────────────────────────────────────────────────────
  // v0.5.1: 锁定倒计时 (服务端返 lockRemaining 秒, 前端 1 秒 1 减)
  // ── v0.5.2: 抽公共 helper 复用
  // ─────────────────────────────────────────────────────────────
  LogInModal.prototype.startPasswordLockCountdown = function (seconds) {
    startPasswordLockCountdown(this, seconds);
  };
}
flarum.reg.add('ziiven-ziven-core', 'forum/components/PhoneLogin', phoneLoginExtension);